/**
 * Retirement episodes — the TypeScript mirror of
 * Lighthouse.Api Contracts/Models/ObservatoryRetirementEpisodeModels.cs, served by
 * `GET /api/observatory/retirement/episodes` and `.../episodes/by-report`.
 *
 * camelCase, as the API serialises. Every enum arrives as its C# NAME (the API has
 * no global enum converter; the controller serves `ToString()`), every C# nullable
 * is `| null`, and every instant carries its UTC offset.
 *
 * DATA CAVEAT, true on every environment today: the table behind this
 * (ReportBuilder.RetirementCandidate) is created by migration
 * AddRetirementCandidate, which is applied nowhere yet — so both endpoints answer
 * 500 until a person applies it. After that the table fills only once the 04:00
 * report-retirement-sweep has run, and the delist and reversal counts stay zero,
 * because automatic delisting is locked in code and nothing can record a delist
 * by decision.
 *
 * The small pure readings at the bottom live here, beside the shapes, because
 * this module imports nothing — so they stay testable in node, and the two
 * components that render episodes cannot disagree about what a state means.
 */

/** RetirementCandidateState, by name. `Open` is the only open state. */
export type RetirementEpisodeState =
  | 'Open'
  | 'Cleared'
  | 'Kept'
  | 'Delisted'
  | 'Rerouted'
  | 'Moot'
  | 'Lapsed'

/** Every state, in the server's (declaration) order — the order of the tabs. */
export const EPISODE_STATES: readonly RetirementEpisodeState[] = [
  'Open', 'Cleared', 'Kept', 'Delisted', 'Rerouted', 'Moot', 'Lapsed',
]

/** DelistBasis, by name: the owner's silence past the grace period, or a person's decision. */
export type DelistBasis = 'OwnerSilence' | 'Decision'

/** DelistReversalKind, by name. */
export type DelistReversalKind = 'Person' | 'EvidenceOfUse' | 'OutOfBand'

/** One enum value by name and how many episodes carry it. A zero is measured. */
export interface RetirementEpisodeCount {
  name: string
  count: number
}

/** Counts over the WHOLE table — never the page or the filter — and the lock sentence. */
export interface RetirementEpisodeSummary {
  totalEpisodes: number
  /** Every state, in order, zeros included. */
  byState: RetirementEpisodeCount[]
  /** OwnerSilence and Decision, kept apart. A reversed delist still counts here. */
  delistsByBasis: RetirementEpisodeCount[]
  /** Person, EvidenceOfUse and OutOfBand. */
  reversalsByKind: RetirementEpisodeCount[]
  /**
   * Which lock holds, in the server's words (RetirementPolicy.AsShipped.WhyNotNow),
   * or null on the day the policy permits an automatic delist. As shipped, never null.
   */
  automaticDelistLockedBecause: string | null
}

/** One episode, as the sweep or a person left it. No person's id is served. */
export interface RetirementEpisode {
  id: string
  savedReportId: string
  /** The report's name when the episode was written. */
  reportNameSnapshot: string
  /** False when the report has been purged since; the two current* fields are then null. */
  reportStillExists: boolean
  currentReportName: string | null
  /** ReportStatus name now, or null when the report no longer exists. */
  currentStatus: string | null
  state: RetirementEpisodeState
  /** RetirementSignal names, in the server's wire order. */
  signals: string[]
  /** The verdict in words, as the sweep wrote it. */
  reason: string
  idleSinceAt: string
  /** RequestRouting name: TargetOwner or CartographerAdmins. */
  routedTo: string
  routedToDisplayName: string | null
  /** Null on every row today: nothing can send a notice until doc 10's notification store exists. */
  firstNotifiedAt: string | null
  adminNotifiedAt: string | null
  /** Always null: retirement has no due date. */
  dueAt: string | null
  createdAt: string
  resolvedAt: string | null
  resolvedByDisplayName: string | null
  resolutionNote: string | null
  keepReason: string | null
  keptUntil: string | null
  delistBasis: DelistBasis | null
  /** The grace in force when an AUTOMATIC delist was applied — per row, never the option. */
  graceDaysApplied: number | null
  reversedAt: string | null
  reversalKind: DelistReversalKind | null
  reversedByDisplayName: string | null
  reversalNote: string | null
}

/** GET /api/observatory/retirement/episodes */
export interface RetirementEpisodes {
  summary: RetirementEpisodeSummary
  rows: RetirementEpisode[]
  /** The filter as applied, or null for every state. */
  state: RetirementEpisodeState | null
  skip: number
  /** After the server's 1..50 clamp. */
  top: number
  /** Episodes matching the filter, over every page. */
  totalCount: number
}

/** What the episodes list is asked. */
export interface RetirementEpisodesQuery {
  state?: RetirementEpisodeState | null
  skip?: number
  top?: number
}

/**
 * The latest episode of one report — GET .../episodes/by-report. A report with no
 * episode is ABSENT from the response, so a lookup miss means "never on the clock".
 */
export interface RetirementEpisodeStateInfo {
  reportId: string
  episodeId: string
  state: RetirementEpisodeState
  createdAt: string
  /** The latest episode's own expiry. */
  keptUntil: string | null
  keepReason: string | null
  reversedAt: string | null
  /** Whether ANY episode of the report protects it past now — the sweep's own reading. */
  hasLiveKeep: boolean
  /** The protection actually in force, across every episode. */
  liveKeepUntil: string | null
  /** That protection is a reversal's quiet period, not a person's keep. */
  liveKeepIsReversalQuietPeriod: boolean
}

/** The server's cap on by-report ids per request: the candidate list's page size. */
export const MAX_EPISODE_REPORT_IDS = 50

// ---------------------------------------------------------------------------
// Pure readings, shared by EpisodeStateCell and RetirementEpisodes
// ---------------------------------------------------------------------------

/** The sentence every Open row without a notice reads. Never a success state. */
export const OPEN_AWAITING_NOTICE =
  'awaiting a notice that cannot be sent yet (no notification channel, doc 10)'

/** The empty delist and reversal sections: not "all clear", a structural impossibility. */
export const NONE_CAN_YET = 'none has happened, and none can yet'

/** A date, without the time — "Kept until" is a day, not an instant. */
function dayLabel(iso: string): string | null {
  const parsed = new Date(iso)
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString()
}

/**
 * What the episode column says for one report, or null for the dash.
 *
 * Only three readings: a live protection (a keep, or a reversal's quiet period —
 * worded apart, because one is a person's reason and the other is the sweep's),
 * an open episode, or nothing. A terminal episode with no live protection has
 * nothing left to say in a column; its state is offered as the dash's title.
 */
export function episodeCellText(state: RetirementEpisodeStateInfo | null): string | null {
  if (state == null) return null
  if (state.state === 'Open') return 'Open episode'
  if (state.hasLiveKeep && state.liveKeepUntil != null) {
    const day = dayLabel(state.liveKeepUntil)
    if (day == null) return null
    return state.liveKeepIsReversalQuietPeriod
      ? `Delist reversed — quiet until ${day}`
      : `Kept until ${day}`
  }
  return null
}

/**
 * The status line of one episode row in the list.
 *
 * An Open row with no owner notice reads the awaiting sentence, because that is
 * the whole truth of every Open row today: the clock cannot start until a notice
 * exists, and no notice can be sent. When doc 10 lands and FirstNotifiedAt is
 * written, the row says when the owner was told instead — from the data, not from
 * a flag in this client.
 */
export function episodeStatusText(row: RetirementEpisode): string {
  if (row.state === 'Open') {
    return row.firstNotifiedAt == null
      ? `Open — ${OPEN_AWAITING_NOTICE}`
      : `Open — owner notified ${dayLabel(row.firstNotifiedAt) ?? 'at an unreadable time'}`
  }
  if (row.state === 'Delisted') {
    const grace = row.graceDaysApplied == null ? 'the grace period' : `a ${row.graceDaysApplied}-day grace period`
    const basis = row.delistBasis === 'OwnerSilence'
      ? `the owner's silence past ${grace}`
      : row.delistBasis === 'Decision' ? "a person's decision" : 'an unrecorded basis'
    return row.reversedAt != null ? `Delisted by ${basis}, then reversed` : `Delisted by ${basis}`
  }
  if (row.state === 'Kept') {
    const until = row.keptUntil == null ? null : dayLabel(row.keptUntil)
    return until == null ? 'Kept' : `Kept until ${until}`
  }
  return EPISODE_STATE_MEANING[row.state]
}

/** Each terminal state in words, for the rows and the tabs' hint. */
export const EPISODE_STATE_MEANING: Record<RetirementEpisodeState, string> = {
  Open: 'On the consent clock, or waiting for the notice that would start it',
  Cleared: 'Stopped qualifying: it ran, a person acted, or it gained a schedule, favourite, request or keep',
  Kept: 'A person said keep it, with a reason and an expiry',
  Delisted: 'Delisted by a decision or by silence past the grace period',
  Rerouted: 'The party it was addressed to changed, so the notice given no longer counts',
  Moot: 'The report left the population another way: purged, archived, retired or deleted',
  Lapsed: 'A notice aged past the window in which it may count as consent',
}

/** The count for one name, or null when the server did not list it (never a guessed zero). */
export function countFor(counts: RetirementEpisodeCount[], name: string): number | null {
  const found = counts.find((c) => c.name === name)
  return found ? found.count : null
}

/** Whether a set of counts is entirely measured zeros — the "none has happened" case. */
export function allZero(counts: RetirementEpisodeCount[]): boolean {
  return counts.length > 0 && counts.every((c) => c.count === 0)
}
